import { useReduxState } from 'hooks';
import { Badge, FlexContainer, MoreOptions } from 'components/common';
import { CardProgressStatus } from 'components/common/tasks';
import {
  AchievementCompletionAddSelf,
  AchievementCompletionCancelRequest,
  AchievementCompletionEditSelf
} from '.';

import type { GetAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: Pick<
    GetAchievementsRes['achievements'][0],
    | 'id'
    | 'certificateAllowAdding'
    | 'certificateRequiredFromUser'
    | 'monthsBeforeExpiration'
    | 'isIncompleteViewableByUser'
    | 'pendingRequest'
    | 'type'
    | 'userAccessRequireApproval'
    | 'userAttributes'
  >;
  isInCard?: boolean;
  isNewRequest?: boolean;
  preventRedirectAfterSubmit?: boolean;
  onUpdate: () => Promise<void>;
}

const AchievementAvailableActions = ({
  achievement,
  isInCard,
  isNewRequest = false,
  preventRedirectAfterSubmit,
  onUpdate
}: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="actions">
      <div>
        {achievement.pendingRequest && isInCard ? (
          <CardProgressStatus
            category="warning"
            icon="hourglass-start"
            label={messages.achievementRequested}
          />
        ) : isNewRequest && !isInCard ? (
          <Badge
            category="warning-light"
            icon="hourglass-start"
            isRounded={false}
            text={messages.achievementRequestedNew}
          />
        ) : null}
      </div>

      {!achievement.pendingRequest ? (
        <AchievementCompletionAddSelf
          achievement={achievement}
          isInCard={isInCard}
          isRequest={achievement.userAccessRequireApproval}
          preventRedirectAfterSubmit={preventRedirectAfterSubmit}
          updateAchievements={onUpdate}
        />
      ) : null}

      {achievement.pendingRequest && isInCard ? (
        <MoreOptions category="lighter" styleContentButtons={false}>
          <AchievementCompletionEditSelf
            achievement={achievement}
            inDropdown={true}
            initialValues={achievement.pendingRequest}
            updateAchievements={onUpdate}
          />

          <AchievementCompletionCancelRequest
            achievementId={achievement.id}
            inDropdown={true}
            requestId={achievement.pendingRequest.id}
            updateAchievements={onUpdate}
          />
        </MoreOptions>
      ) : achievement.pendingRequest ? (
        <FlexContainer style={{ gap: 10 }}>
          <AchievementCompletionEditSelf
            achievement={achievement}
            initialValues={achievement.pendingRequest}
            updateAchievements={onUpdate}
          />

          <AchievementCompletionCancelRequest
            achievementId={achievement.id}
            requestId={achievement.pendingRequest.id}
            updateAchievements={onUpdate}
          />
        </FlexContainer>
      ) : null}

      <style jsx>{`
        .actions {
          align-items: center;
          border-top: 1px solid ${theme.lightGrey};
          display: flex;
          justify-content: space-between;
          flex-grow: 0;
          flex-shrink: 0;
          gap: 10px;
          padding: 15px;
        }
      `}</style>
    </div>
  );
};

export default AchievementAvailableActions;
